import { useEffect, useMemo, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {lastTransactionStatusConstants} from '../../state/constants';
import { useLocation } from 'react-router-dom';
import { firebaseAnalytics, firebaseEvents } from '../../snippets/firebase';
import { initializePendo } from '../../snippets/pendo';
import {useAvailableDataLoading, useCreditAccountEndpointEnabled, useCurrentCompany, useCurrentUser, useEmployee, useIsAccountingAvailable, useIsAccountingExportMode, useIsAdmin, useIsAuthenticated, useIsBudgetsAvailable, useIsFeaturesLoaded, useIsManager, useIsPsd2ObAvailable, useIsTreezorEnabled} from '../../hooks';
import { cardsHelpers, locationHelpers, objectHelpers, systemHelpers } from '../../utils/helpers';
import { accountingActions } from '../../state/actions/accounting.actions';
import { bankingActions } from '../../state/actions/banking.actions';
import {budgetActions} from '../../state/actions/budget.actions';
import { companyActions } from '../../state/actions/company.actions';
import { mainActions } from '../../state/actions/main.actions';
import { openBankingActions } from '../../state/actions/openBanking.actions';
import { overviewActions } from '../../state/actions/overview.actions';
import { transactionActions } from '../../state/actions/transaction.actions';
import { userActions } from '../../state/actions/user.actions';
import { history } from '../../state/history';
import { availableModulesConstants, localStorageKeysConstants, userRolesConstants } from '../../constants';
import routesList from '../../routes/routes.json';

const isProduction = systemHelpers.isProduction();

const {CARDS, TRANSACTIONS, SUBSCRIPTIONS} = availableModulesConstants;

const INTERNAL_COMPANY_ID = 'a8f138bdad7f432e890dba8163c99805';
const ROLE_LABELS = {
  [userRolesConstants.USER]: 'user',
  [userRolesConstants.MANAGER]: 'manager',
  [userRolesConstants.ADMIN]: 'admin',
  [userRolesConstants.BOOKKEEPER]: 'bookkeeper',
};

const SCREEN_NAMES = {
  '/': 'Overview',
  '/expenses': 'Expenses',
  '/cards': 'Cards',
  '/vendors': 'Vendors',
  '/account': 'Transactions',
  '/profile': 'Profile',
  '/support': 'Support',
  '/settings': 'Settings',
  '/budgets/active': 'BudgetsActive',
  '/budgets/requests': 'BudgetsRequests',
};

const HASH_TAB_SCREENS = {
  '/settings': {
    '#overview': 'Settings_Overview',
    '#team': 'Settings_Team',
    '#expenses': 'Settings_Expenses',
    '#procurement': 'Settings_Procurement',
    '#properties': 'Settings_Properties',
    '#legal': 'Settings_Legal',
    '#billing': 'Settings_Billing',
    default: 'Settings_Overview',
  },
  '/subscriptions': {
    '#ALL': 'Subscriptions_All',
    '#AUTHORIZED': 'Subscriptions_Authorized',
    '#NEED_REVIEW': 'Subscriptions_NeedReview',
    '#RESTRICTED': 'Subscriptions_Restricted',
    '#UNMANAGED': 'Subscriptions_Unmanaged',
    '#DELETED': 'Subscriptions_Deleted',
    default: 'Subscriptions_All',
  },
  '/invoices': {
    '#all': 'Invoices_All',
    '#unpaid': 'Invoices_Unpaid',
    '#to_reconcile': 'Invoices_ToReconcile',
    '#paid': 'Invoices_Paid',
    default: 'Invoices_All',
  },
  '/netsuite/journal-entries': {
    '#missing_bills': 'NetSuiteJournalEntries_MissingBills',
    '#not_on_erp': 'NetSuiteJournalEntries_NotOnErp',
    '#ready_to_sync': 'NetSuiteJournalEntries_ReadyToSync',
    '#synced': 'NetSuiteJournalEntries_Synced',
    default: 'NetSuiteJournalEntries_MissingBills',
  },
  '/netsuite/vendor-bills': {
    '#not_on_erp': 'NetSuiteVendorBills_NotOnErp',
    '#synced': 'NetSuiteVendorBills_Synced',
    '#erp_bills': 'NetSuiteVendorBills_ErpBills',
    default: 'NetSuiteVendorBills_NotOnErp',
  },
  '/netsuite/vendors': {
    '#erp_vendors': 'NetSuiteVendors_ErpVendors',
    '#not_on_erp': 'NetSuiteVendors_NotOnErp',
    '#synced': 'NetSuiteVendors_Synced',
    default: 'NetSuiteVendors_NotOnErp',
  },
};

const getScreenName = (pathname, hash) => {
  if (HASH_TAB_SCREENS[pathname]) {
    const tabs = HASH_TAB_SCREENS[pathname];
    return tabs[hash] ?? tabs.default;
  }
  if (SCREEN_NAMES[pathname]) return SCREEN_NAMES[pathname];
  if (pathname.startsWith('/cards/')) return 'CardDetails';
  if (pathname.startsWith('/subscriptions/')) return 'SubscriptionDetails';
  if (pathname.startsWith('/invoices/')) return 'InvoiceDetails';
  if (pathname.startsWith('/budgets/active/')) return 'BudgetActiveDetails';
  if (pathname.startsWith('/budgets/requests/')) return 'BudgetRequestDetails';
  if (pathname.startsWith('/team/')) return 'TeamUserDetails';
  return null;
};

const UserCompanyInitializer = ({handleDisableDataLoading}) => {
  const dispatch = useDispatch();
  const lastTransactionStatus = useSelector((state) => state.transaction.lastTransactionStatus);

  const employee = useEmployee();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isAuthenticated = useIsAuthenticated();
  const user = useCurrentUser();

  const [accountingDataLoadedCompanyId, setAccountingDataLoadedCompanyId] = useState(null);
  const [creditAccountsLoadedCompanyId, setCreditAccountsLoadedCompanyId] = useState(null);
  const [startPathname, setStartPathname] = useState(null);

  const availableDataLoading = useAvailableDataLoading();

  const currentCompany = useCurrentCompany();

  const isAccountingAvailable = useIsAccountingAvailable();
  const isAccountingExportMode = useIsAccountingExportMode();
  const isTreezorEnabled = useIsTreezorEnabled();
  const isBudgetsAvailable = useIsBudgetsAvailable();
  const isFeaturesLoaded = useIsFeaturesLoaded();
  const isPsd2ObAvailable = useIsPsd2ObAvailable();
  const isCreditAccountEndpointAvailable = useCreditAccountEndpointEnabled();

  const companyId = useMemo(() => currentCompany?.id, [currentCompany]);

  const location = useLocation();

  const setAvailableModules = (modules) => dispatch(mainActions.setAvailableModules(modules));

  useEffect(() => {
    if (isAuthenticated) {
      firebaseAnalytics.logEvent(firebaseEvents.USER_AUTHENTICATED);
    }
  }, [isAuthenticated, user]);

  // Keyed on employee/companyId (not user) because those resolve after auth;
  // folding them into the effect above left user_role as 'unknown' and
  // mislabeled internal staff. PII (email/name) is intentionally not sent to GA4.
  useEffect(() => {
    if (!isAuthenticated) return;
    const isInternal =
      employee?.role === userRolesConstants.SUPPORT ||
      companyId === INTERNAL_COMPANY_ID;
    firebaseAnalytics.setUserProperties({
      user_role: ROLE_LABELS[employee?.role] ?? 'unknown',
      is_internal: isInternal ? 'true' : 'false',
    });
  }, [isAuthenticated, employee, companyId]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const screenName = getScreenName(location.pathname, location.hash);
    if (screenName) {
      firebaseAnalytics.logEvent('screen_view', { screen_name: screenName });
    }
  }, [location.pathname, location.hash, isAuthenticated]);

  useEffect(() => {
    if (!isProduction) return;

    if (isAuthenticated) {
      initializePendo({
        company: currentCompany,
        user: employee
      });
    }
  }, [currentCompany?.id, employee, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isBudgetsAvailable && currentCompany?.id) {
      dispatch(budgetActions.getBudgets());
    }
  }, [isBudgetsAvailable, currentCompany?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the OB account access list whenever OB is available and the employee is
  // known. This populates the OB payment method filter options and the scoped
  // accountAccessIds for every role (admins included) — accounts are only visible
  // to their owner and to added collaborators, so account/connection loads stay
  // scoped to the returned ids for everyone (H1MVP-4452).
  // Waits on both isFeaturesLoaded and employee before settling anything: isPsd2ObAvailable
  // reads false both before features load and once loaded with OB genuinely unavailable, and
  // employee can still resolve after features under network delay — either gap left open would
  // let the "no access" branch below fire early and mark accountAccessLoaded=true before the
  // real answer is known, ahead of the OverviewRouterPage tier decision that reads it.
  useEffect(() => {
    if (!isFeaturesLoaded || objectHelpers.isEmptyObject(employee)) return;
    if (!isPsd2ObAvailable) {
      dispatch(openBankingActions.setAccountAccess());
      dispatch(openBankingActions.setObPaymentMethods());
      return;
    }
    dispatch(openBankingActions.getAccountAccessList());
  }, [isFeaturesLoaded, isPsd2ObAvailable, isAdmin, employee?.id, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated && typeof startPathname === 'string' && startPathname?.trim()) {
      history.push(startPathname);
    }
  }, [isAuthenticated, startPathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { hash, pathname, search } = history.location;
    const isAuthRoute = [routesList.signUp, routesList.login, routesList.forgotPin].includes(pathname);

    if (!isAuthRoute) {
      setStartPathname(`${pathname}${search}${hash}`);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId) {
      dispatch(userActions.getNotificationsListWithCount());
      dispatch(userActions.getEmployeePopupSettings());
      dispatch(companyActions.getCompanyCurrentCurrency());
      dispatch(companyActions.getCompanyFeatures());
      dispatch(companyActions.getEmployees());
      dispatch(companyActions.getTags());
    }
  }, [currentCompany?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also keyed on the status: changeCompany clears it even for the company already selected (a
  // re-select), and nothing else would fetch it again — the Transactions first load waits on it.
  useEffect(() => {
    if (companyId && isTreezorEnabled && lastTransactionStatus === lastTransactionStatusConstants.IDLE) {
      dispatch(transactionActions.getLastTransaction());
    }
  }, [currentCompany?.id, isTreezorEnabled, lastTransactionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // The accounting settings carry the company's accounting MODE, which gates surfaces outside the
  // accounting pages — the Company Settings > Accounting tab, and the section labelling. So unlike
  // the reference data below, this cannot wait for the user to open /netsuite first: on a cold load
  // straight to /settings the flag would never arrive and the tab would silently not exist.
  useEffect(() => {
    if (companyId && isAccountingAvailable) {
      dispatch(accountingActions.getCompanySettings());
    }
  }, [currentCompany?.id, isAccountingAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // The company's own ERP field definitions decide which columns the accounting tables render, so
  // they are needed on those pages and not only on the settings tab that manages them. Gated on
  // export mode because a NetSuite company has none and should not be asked for them; that flag
  // arrives with the settings above, so this runs a beat later.
  useEffect(() => {
    if (companyId && isAccountingExportMode) {
      dispatch(accountingActions.getAccountingFields());
    }
  }, [currentCompany?.id, isAccountingExportMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Checks if the path starts with /accounting, verifies if accounting data for the current company is already loaded,
  // and if not, loads accounting data and marks it as loaded.
  useEffect(() => {
    if (companyId !== accountingDataLoadedCompanyId) {
      setAccountingDataLoadedCompanyId(null); // Reset when company changes
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // accounting data loading
    const isAccountingPath = location.pathname.startsWith('/netsuite');
    if (
      isAccountingAvailable &&
      isAccountingPath &&
      companyId &&
      accountingDataLoadedCompanyId !== companyId
    ) {
      dispatch(accountingActions.getCostCenters());
      dispatch(accountingActions.getCustomSegments());
      dispatch(accountingActions.getListItems());
      dispatch(accountingActions.getLocations());
      dispatch(accountingActions.getExpenseCategories());
      dispatch(accountingActions.getNumberingFlows());
      dispatch(accountingActions.getTaxCodes());
      dispatch(accountingActions.getVendorExpenseAccounts());
      setAccountingDataLoadedCompanyId(companyId); // Mark as loaded
    }
  }, [companyId, location.pathname, accountingDataLoadedCompanyId, isAccountingAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loaded separately from the main accounting-data effect because the flag comes
  // from `companySettings`, which is fetched asynchronously above and is therefore
  // not yet available on its first run. This effect re-runs once the setting arrives.
  useEffect(() => {
    if (
      isAccountingAvailable &&
      location.pathname.startsWith('/netsuite') &&
      companyId &&
      isCreditAccountEndpointAvailable &&
      creditAccountsLoadedCompanyId !== companyId
    ) {
      dispatch(accountingActions.getCreditAccounts());
      setCreditAccountsLoadedCompanyId(companyId);
    }
  }, [companyId, location.pathname, isAccountingAvailable, isCreditAccountEndpointAvailable, creditAccountsLoadedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const token = localStorage.getItem(localStorageKeysConstants.TOKEN);
    if (!token) {
      dispatch(userActions.markedCheckedAuth());
      return;
    }

    const { pathname } = history.location;
    const hideLoader = !pathname.startsWith('/email/verification');
    const isPublicPath = locationHelpers.isActivePathInList(
      pathname,
      systemHelpers.getPublicPaths()
    );

    if (isPublicPath) {
      handleDisableDataLoading?.();
      return;
    }

    if (availableDataLoading) {
      dispatch(userActions.getEmployeeProfile());
    }

    dispatch(userActions.getUserProfile(hideLoader));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // initialize banking user
  useEffect(() => {
    if (objectHelpers.isEmptyObject(employee) || !isTreezorEnabled) return;

    const {id} = employee;
    const bankingModules = [CARDS, SUBSCRIPTIONS];

    const checkUserAccess = () => dispatch(bankingActions.getUserAccessToCards(id));

    const handleCompanyState = (companyState) => {
      const isEnabledBanking = cardsHelpers.checkIsAvailableBanking(companyState);
      if (isEnabledBanking) bankingModules.push(TRANSACTIONS);
      if (isManager || isAdmin) {
        setAvailableModules(bankingModules);
        isEnabledBanking && checkUserAccess();
        return;
      }

      getCardList(id, handleCardListResponse);
    };

    const handleCardListResponse = (data) => {
      if (data.length > 0) {
        setAvailableModules(bankingModules);
        checkUserAccess();
        return;
      }

      // If no cards exist, proceed with verification
      finishCardVerificationProcess();
      dispatch(overviewActions.getOverviewSubscriptionsTotals(null, handleSubscriptionsResponse));
    };

    const handleSubscriptionsResponse = (subscriptions) => {
      if ((subscriptions?.total || 0) > 0) {
        setAvailableModules([SUBSCRIPTIONS]);
      }
    };

    dispatch(bankingActions.getCompanyState({
      successCallback: handleCompanyState,
      errorCallback: () => {
        finishCardVerificationProcess();
        setAvailableModules([]);
      }
    }));

    dispatch(bankingActions.getCompanyDetails());
  }, [employee, isTreezorEnabled, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishCardVerificationProcess = () => {
    dispatch(bankingActions.updateCardVerificationStatus(false, null, false));
  }

  const getCardList = (id, successFunc, errorFunc) => {
    dispatch(bankingActions.getUserCardList(
      id,
      null,
      (data) => successFunc && successFunc(data),
      (data) => errorFunc && errorFunc(data)
    ));
  }

  return null;
};

export default UserCompanyInitializer;
